/**
 * Cliente de la API de Cargadores / Estaciones (tag admin del `openapi.yaml`) — Admin Web.
 *
 * Endpoints (US-2.2):
 * - GET    /api/v1/admin/stations
 * - POST   /api/v1/admin/stations
 * - GET    /api/v1/admin/stations/{stationId}
 * - PATCH  /api/v1/admin/stations/{stationId}
 * - DELETE /api/v1/admin/stations/{stationId}
 * - POST   /api/v1/admin/stations/{stationId}/sync (reintento de sync con el core)
 *
 * Conectores (subrecurso del cargador):
 * - GET    /api/v1/admin/stations/{stationId}/connectors
 * - POST   /api/v1/admin/stations/{stationId}/connectors
 * - GET    /api/v1/admin/stations/{stationId}/connectors/{connectorId}
 * - PATCH  /api/v1/admin/stations/{stationId}/connectors/{connectorId}
 * - DELETE /api/v1/admin/stations/{stationId}/connectors/{connectorId}
 *
 * Tipos derivados del contrato en `@goelectric/shared-types`; mismo patrón de red
 * que `locationApi` (sobre `apiRequest`).
 */
import type {
  AdminConnector,
  AdminConnectorDetail,
  AdminStation,
  AdminStationsPage,
  CreateConnectorBody,
  CreateStationBody,
  ListStationsQuery,
  UpdateConnectorBody,
  UpdateStationBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (filtros + offset/limit), tal como los define el contrato. */
export type StationListParams = ListStationsQuery;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: StationListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** `stationId` / `connectorId` van en la URL: siempre codificados. */
const stationsPath = '/api/v1/admin/stations';
const stationPath = (stationId: string) => `${stationsPath}/${encodeURIComponent(stationId)}`;
const connectorsPath = (stationId: string) => `${stationPath(stationId)}/connectors`;
const connectorPath = (stationId: string, connectorId: number) =>
  `${connectorsPath(stationId)}/${encodeURIComponent(String(connectorId))}`;

export const stationApi = {
  /* ----------------------------------------------------------------------- */
  /* Estaciones                                                               */
  /* ----------------------------------------------------------------------- */

  list(params: StationListParams = {}): Promise<AdminStationsPage> {
    return apiRequest<AdminStationsPage>(`${stationsPath}${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(stationId: string): Promise<AdminStation> {
    return apiRequest<AdminStation>(stationPath(stationId));
  },

  create(body: CreateStationBody): Promise<AdminStation> {
    return apiRequest<AdminStation>(stationsPath, { method: 'POST', body });
  },

  update(stationId: string, body: UpdateStationBody): Promise<AdminStation> {
    return apiRequest<AdminStation>(stationPath(stationId), { method: 'PATCH', body });
  },

  remove(stationId: string): Promise<void> {
    return apiRequest<void>(stationPath(stationId), { method: 'DELETE' });
  },

  /** Reintenta la sincronización con el core CitrineOS; devuelve el cargador actualizado. */
  sync(stationId: string): Promise<AdminStation> {
    return apiRequest<AdminStation>(`${stationPath(stationId)}/sync`, { method: 'POST' });
  },

  /* ----------------------------------------------------------------------- */
  /* Conectores (subrecurso)                                                  */
  /* ----------------------------------------------------------------------- */

  listConnectors(stationId: string): Promise<AdminConnector[]> {
    return apiRequest<AdminConnector[]>(connectorsPath(stationId));
  },

  getConnector(stationId: string, connectorId: number): Promise<AdminConnectorDetail> {
    return apiRequest<AdminConnectorDetail>(connectorPath(stationId, connectorId));
  },

  createConnector(stationId: string, body: CreateConnectorBody): Promise<AdminConnector> {
    return apiRequest<AdminConnector>(connectorsPath(stationId), { method: 'POST', body });
  },

  updateConnector(
    stationId: string,
    connectorId: number,
    body: UpdateConnectorBody,
  ): Promise<AdminConnector> {
    return apiRequest<AdminConnector>(connectorPath(stationId, connectorId), {
      method: 'PATCH',
      body,
    });
  },

  removeConnector(stationId: string, connectorId: number): Promise<void> {
    return apiRequest<void>(connectorPath(stationId, connectorId), { method: 'DELETE' });
  },
};
